import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { AppShell } from './components/AppShell';
import { AcceptanceGate } from './components/AcceptanceGate';
import { Skeleton } from './components/ui';
import { LoginPage } from './pages/Login';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { ChangePasswordPage } from './pages/ChangePassword';
import { DashboardPage } from './pages/Dashboard';
import { SuppliersPage } from './pages/admin/Suppliers';
import { SupplierProfilePage } from './pages/admin/SupplierProfile';
import { NonconformitiesPage } from './pages/Nonconformities';
import { CertificationsPage } from './pages/admin/Certifications';
import { PeriodsPage } from './pages/admin/Periods';
import { PeriodDetailPage } from './pages/admin/PeriodDetail';
import { SemesterPage } from './pages/admin/Semester';
import { ImportsPage } from './pages/admin/Imports';
import { EmailsPage } from './pages/admin/Emails';
import { UsersPage } from './pages/admin/Users';
import { ReportPage } from './pages/Report';
import { RncDetailPage } from './pages/RncDetail';
import { SupplierCertificationsPage } from './pages/SupplierCertifications';
import { DocumentsPage } from './pages/Documents';
import { MyCompanyPage } from './pages/MyCompany';
import { UserRole } from '@weloze/shared';

/**
 * Guarda de rota do lado do cliente.
 *
 * É conveniência de navegação, **não** segurança: quem manda é o servidor, que
 * checa a sessão e o escopo de loja em cada request. Um usuário que force a URL
 * aqui só veria uma casca vazia — os dados não vêm.
 */
function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-10 sm:px-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /*
   * O aceite fica **dentro** do shell, e não no lugar dele.
   *
   * O cabeçalho continua na tela com o seletor de loja e o botão de sair: quem
   * responde por duas empresas precisa trocar de loja durante o bloqueio, e
   * ninguém pode ficar preso numa tela sem saída.
   */
  return (
    <AppShell>
      <AcceptanceGate>{children}</AcceptanceGate>
    </AppShell>
  );
}

/**
 * Telas da Weloze. Como o `Protected`, é conveniência de navegação: o servidor
 * recusa a requisição de qualquer jeito, com 403 ou 404. Aqui só evitamos
 * mostrar uma tela que voltaria vazia.
 */
function WelozeOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (!loading && user && user.role === UserRole.SUPPLIER_USER) {
    return <Navigate to="/" replace />;
  }
  return <Protected>{children}</Protected>;
}

/**
 * Telas exclusivas do administrador.
 *
 * O `WelozeOnly` não serve aqui: o perfil de consulta enxerga a carteira
 * inteira, mas a lista de usuários diz **quem tem acesso a quê** — é o mapa que
 * alguém precisaria para escolher uma conta a atacar. O servidor recusa com 403
 * de qualquer forma; isto evita oferecer a porta.
 */
function AdminOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (!loading && user && user.role !== UserRole.WELOZE_ADMIN) {
    return <Navigate to="/" replace />;
  }
  return <Protected>{children}</Protected>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
          <Route path="/definir-senha/:token" element={<ResetPasswordPage />} />

          <Route
            path="/"
            element={
              <Protected>
                <DashboardPage />
              </Protected>
            }
          />
          <Route
            path="/admin/fornecedores"
            element={
              <WelozeOnly>
                <SuppliersPage />
              </WelozeOnly>
            }
          />
          <Route
            path="/admin/fornecedores/:storeId"
            element={
              <WelozeOnly>
                <SupplierProfilePage />
              </WelozeOnly>
            }
          />

          {/* A lista de RNCs é dos dois lados, e por isso saiu de `/admin`: o
              fornecedor precisa chegar ao 8D, e o servidor já devolve só as
              lojas dele. `/admin/rncs` continua respondendo, para não quebrar
              link antigo nem favorito. */}
          <Route
            path="/rncs"
            element={
              <Protected>
                <NonconformitiesPage />
              </Protected>
            }
          />
          <Route path="/admin/rncs" element={<Navigate to="/rncs" replace />} />
          <Route
            path="/admin/certificacoes"
            element={
              <WelozeOnly>
                <CertificationsPage />
              </WelozeOnly>
            }
          />

          <Route
            path="/admin/competencias"
            element={
              <WelozeOnly>
                <PeriodsPage />
              </WelozeOnly>
            }
          />
          <Route
            path="/admin/competencias/:year/:month"
            element={
              <WelozeOnly>
                <PeriodDetailPage />
              </WelozeOnly>
            }
          />
          {/* O ranking da competência é a mesma tabela do detalhe: uma rota só,
              para as duas telas não divergirem (PLAN §7, telas 8 e 12). */}
          <Route
            path="/admin/ranking/:year/:month"
            element={<Navigate to="/admin/competencias" replace />}
          />
          <Route
            path="/admin/competencias/semestre/:year/:half"
            element={
              <WelozeOnly>
                <SemesterPage />
              </WelozeOnly>
            }
          />

          <Route
            path="/admin/importacao"
            element={
              <WelozeOnly>
                <ImportsPage />
              </WelozeOnly>
            }
          />

          {/* Ler o registro e' consulta; disparar e' ato, e o botao so aparece
              para o administrador dentro da propria tela. */}
          <Route
            path="/admin/usuarios"
            element={
              <AdminOnly>
                <UsersPage />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/emails"
            element={
              <WelozeOnly>
                <EmailsPage />
              </WelozeOnly>
            }
          />

          {/* A ficha vista pelo proprio fornecedor. A Weloze tambem alcanca, e
              ali ve exatamente o que ele ve. */}
          <Route
            path="/minha-empresa"
            element={
              <Protected>
                <MyCompanyPage />
              </Protected>
            }
          />

          {/* Para os dois lados: o manual e os formularios sao os mesmos para
              toda a carteira, e quem publica e a Weloze. */}
          <Route
            path="/documentos"
            element={
              <Protected>
                <DocumentsPage />
              </Protected>
            }
          />

          {/* Do fornecedor, e por isso fora de `/admin`: a Weloze tem a fila de
              validação em `/admin/certificacoes`, que é outra pergunta. */}
          <Route
            path="/certificados"
            element={
              <Protected>
                <SupplierCertificationsPage />
              </Protected>
            }
          />

          {/* Mesma rota para fornecedor e Weloze: o 8D e preenchido pelos dois
              lados, e duas telas para o mesmo formulario divergiriam no primeiro
              campo novo. O isolamento fica no servidor. */}
          <Route
            path="/rncs/:rncId"
            element={
              <Protected>
                <RncDetailPage />
              </Protected>
            }
          />

          {/* Mesma rota para fornecedor e Weloze (PLAN §7.1). O isolamento
              fica no servidor: loja fora do escopo responde 404. */}
          <Route
            path="/relatorio/:storeId/:year/:month"
            element={
              <Protected>
                <ReportPage />
              </Protected>
            }
          />

          <Route
            path="/perfil/senha"
            element={
              <Protected>
                <ChangePasswordPage />
              </Protected>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
